import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppStore } from '../../App';
import ProposalCreate from './ProposalCreate';
import ReportButton from './ReportButton';
import InvitationCreateModal from './InvitationCreateModal';
import BarterProposeModal from './BarterProposeModal';
import ListingEditModal from './ListingEditModal';
import { FamilyAvatar } from '../../components/FamilyAvatar';
import { ScreenPublicModal } from '../../components/ScreenPublicModal';
import { formatWon, priceTooltipText } from '../../utils/number';

interface Video {
 file_hash: string;
 filename: string;
 mime_type?: string;
 duration?: number;
 thumbnail_path?: string;
}

interface Screen {
 client_hash: string;
 name: string;
 address?: string | null;
 latitude?: number | null;
 longitude?: number | null;
 thumbnail_url?: string | null;
 photo_count: number;
}

interface ListingDetailData {
 listing: {
  listing_hash: string;
  mo_family_hash: string;
  side: 'seeking_advertiser' | 'seeking_media' | 'seeking_member' | 'barter';
  title: string;
  description?: string | null;
  period_start: string;
  period_end: string;
  price_per_impression: string;
  currency: string;
  industry_ksic_major?: string | null;
  region_sido?: string | null;
  region_gugun?: string | null;
  daily_budget_cap?: string | number | null;
  guaranteed_impressions?: number | null;
  is_active: boolean;
  is_expired?: boolean;
 };
 family: { mo_family_hash: string; name: string; description?: string; image_url?: string | null };
 videos: Video[];
 /** 소유주(+구성원) 측 영상 — 로그인 시에만 채워짐. playing=자기 화면에 재생 중, outbound=외부 화면으로 내보내는 중. */
 owner_videos?: { playing: Video[]; outbound: (Video & { screen_family_count: number })[] };
 screens: { items: Screen[]; total: number; page: number; limit: number };
 owner: {
  mo_peer_hash: string;
  name: string;
  business_name: string | null;
  profile_image: string | null;
  // 비로그인엔 null
  email: string | null;
  phone: string | null;
  registration_number: string | null;
  address: string | null;
  ceo_name: string | null;
 } | null;
}

const ListingDetail: React.FC = () => {
 const { listingHash } = useParams<{ listingHash: string }>();
 const navigate = useNavigate();
 const { user } = useAppStore();
 const [data, setData] = useState<ListingDetailData | null>(null);
 const [error, setError] = useState<string | null>(null);
 const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
 const [showProposalModal, setShowProposalModal] = useState(false);
 const [showInvitationModal, setShowInvitationModal] = useState(false);
 const [showBarterModal, setShowBarterModal] = useState(false);
 const [showEditModal, setShowEditModal] = useState(false);
 const [industryMap, setIndustryMap] = useState<Record<string, string>>({});

 useEffect(() => {
  fetch('/market/listings/meta/industries')
   .then(r => r.json())
   .then(j => {
    const m: Record<string, string> = {};
    for (const it of (j.data || [])) m[it.code] = it.ko_name;
    setIndustryMap(m);
   })
   .catch(() => {});
 }, []);
 const [screenPage, setScreenPage] = useState(0);
 const screenLimit = 12;
 const [selectedScreen, setSelectedScreen] = useState<string | null>(null);
 /** 소유주 영상 썸네일 클릭 → 인라인 미리보기 (매물 전용 재생 경로, 2026-10-14). */
 const [previewVideo, setPreviewVideo] = useState<Video | null>(null);
 const [previewFailed, setPreviewFailed] = useState(false);

 useEffect(() => {
  if (!listingHash) return;
  const params = new URLSearchParams({ screen_page: String(screenPage), screen_limit: String(screenLimit) });
  fetch(`/market/listings/${listingHash}?${params.toString()}`, { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    if (!j.success) { setError(j.error || '조회 실패'); return; }
    setData(j.data);
    setIsLoggedIn(j.data.owner?.email !== null && j.data.owner?.email !== undefined);
   })
   .catch(err => setError(err.message || '조회 실패'));
 }, [listingHash, screenPage]);

 if (error) return <div className="bg-red-50 text-red-700 p-3">{error}</div>;
 if (!data) return <div className="text-gray-500 py-8 text-center">로딩 중...</div>;

 const { listing, family, owner, videos, screens } = data;
 const ownerVideos = data.owner_videos ?? { playing: [], outbound: [] };
 const sideLabel =
  listing.side === 'seeking_advertiser' ? '광고를 받겠습니다' :
  listing.side === 'seeking_media' ? '광고를 보낼 곳을 찾습니다' :
  listing.side === 'barter' ? '슬롯 교환 (바터) 매물' :
  '구성원 모집';

 return (
  <div>
   <button
    onClick={() => navigate(-1)}
    className="text-blue-600 hover:text-blue-800 mb-4 text-sm"
   >
    ← 뒤로
   </button>

   <div>
    <div className="flex items-start justify-between gap-2 mb-3">
     <div className="flex-1 min-w-0">
      <h1 className="text-lg md:text-2xl font-bold mb-1 break-words">{listing.title}</h1>
      <p className="text-xs md:text-sm text-gray-500">{sideLabel}</p>
     </div>
     <div className="flex flex-col items-end gap-1 flex-shrink-0">
      {listing.is_expired && <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600">만료</span>}
      {!listing.is_active && !listing.is_expired && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100">비활성</span>}
     </div>
    </div>

    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm mb-4">
     <div><dt className="text-gray-500">기간</dt><dd>{String(listing.period_start).slice(0, 10)} ~ {String(listing.period_end).slice(0, 10)}</dd></div>
     {listing.side !== 'seeking_member' && listing.side !== 'barter' && (
      <div>
       <dt className="text-gray-500">단가</dt>
       <dd>
        <span
         className="cursor-help underline decoration-dotted decoration-gray-400"
         title={priceTooltipText(listing.price_per_impression)}
        >
         {formatWon(listing.price_per_impression)}/회
        </span>
       </dd>
      </div>
     )}
     {listing.daily_budget_cap != null && (
      <div>
       <dt className="text-gray-500">하루 최대 책정 금액</dt>
       <dd
        className="cursor-help underline decoration-dotted decoration-gray-400"
        title={'광고주 안전 장치 — 그날 송출이 캡을 넘어도 청구는 캡까지만. 송출은 계속 (잉여분은 미디어사 무상 노출).'}
       >
        {formatWon(Number(listing.daily_budget_cap))}/일
       </dd>
      </div>
     )}
     {listing.guaranteed_impressions != null && (
      <div>
       <dt className="text-gray-500">송출 횟수 보장</dt>
       <dd
        className="cursor-help underline decoration-dotted decoration-gray-400"
        title={'기간 종료 시점:\n· 50% 미만 → 전액 환불\n· 50~100% → 비례 청구\n· 100% 이상 → 보장치 × 단가 정액'}
       >
        {Number(listing.guaranteed_impressions).toLocaleString()}회
       </dd>
      </div>
     )}
     <div>
      <dt
       className="text-gray-500 cursor-help underline decoration-dotted decoration-gray-400 inline-block"
       title={'스크린이 설치된 장소의 업종 (또는 광고주의 사업 분류). 광고주·미디어 매칭 정확도를 높이기 위해 대분류로 선택.'}
      >매장 분류</dt>
      <dd>
       {listing.industry_ksic_major && industryMap[listing.industry_ksic_major]
        ? industryMap[listing.industry_ksic_major]
        : '-'}
      </dd>
     </div>
     <div><dt className="text-gray-500">지역</dt><dd>{listing.region_sido ?? ''} {listing.region_gugun ?? ''}</dd></div>
    </dl>

    {listing.description && (
     <div className="text-sm text-gray-700 whitespace-pre-wrap mb-4">{listing.description}</div>
    )}

    {(() => {
     const isMine = !!user && !!owner?.mo_peer_hash && user.moPeerHash === owner.mo_peer_hash;
     const canMessage = !!isLoggedIn && !!owner?.email && !isMine;
     const goCompose = () => {
      if (!isLoggedIn) {
       alert('메시지를 보내려면 로그인이 필요합니다.');
       return;
      }
      if (!owner?.email) return;
      const params = new URLSearchParams({
       compose_to: owner.email,
       subject: `[마켓] ${listing.title} 관련 문의`,
      });
      navigate(`/momail?${params.toString()}`);
     };
     const goFamily = () => navigate(`/mofamily/${family.mo_family_hash}`);
     return (
      <div className="border-t border-gray-100 pt-4 mt-4">
       <h2 className="text-sm font-semibold text-gray-700 mb-2">모패밀리</h2>
       <div className="flex items-start gap-3">
        <button
         type="button"
         onClick={goFamily}
         className="flex-shrink-0 transition-transform hover:scale-105"
         title={`${family.name} 상세 보기`}
         aria-label="모패밀리 상세 보기"
        >
         <FamilyAvatar
          name={family.name}
          familyHash={family.mo_family_hash}
          imageUrl={family.image_url}
          size={40}
         />
        </button>
        <div className="text-sm flex-1 min-w-0">
         <button
          type="button"
          onClick={goFamily}
          className="font-medium hover:text-blue-600 hover:underline text-left"
         >
          {family.name}
         </button>
         {family.description && <div className="text-gray-500 mt-1">{family.description}</div>}
         {canMessage && (
          <button
           type="button"
           onClick={goCompose}
           className="text-xs text-blue-600 hover:underline mt-2"
          >
           메시지 보내기
          </button>
         )}
         {isMine && (
          <div className="text-xs text-gray-400 mt-1">내가 소유한 모패밀리</div>
         )}
        </div>
       </div>
      </div>
     );
    })()}

    {/* 광고주 매물 외엔 스크린이 없어도 섹션을 보여 "연결된 스크린 없음"을 알린다(2026-10-13). */}
    {listing.side !== 'seeking_media' && (!screens || screens.total === 0) && (
     <div className="border-t border-gray-100 pt-4 mt-4">
      <h2 className="text-sm font-semibold text-gray-700 mb-2">스크린</h2>
      <p className="text-sm text-gray-400">연결된 스크린이 없습니다.</p>
     </div>
    )}
    {screens && screens.total > 0 && (() => {
     const totalPages = Math.max(1, Math.ceil(screens.total / screens.limit));
     return (
      <div className="border-t border-gray-100 pt-4 mt-4">
       <h2 className="text-sm font-semibold text-gray-700 mb-2">
        스크린 <span className="text-gray-400 font-normal">{screens.total}대</span>
       </h2>
       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3">
        {screens.items.map(s => (
         <button
          key={s.client_hash}
          type="button"
          onClick={() => setSelectedScreen(s.client_hash)}
          className="bg-gray-50 border border-gray-200 overflow-hidden flex flex-col text-left hover:border-blue-400 transition-colors"
         >
          {s.thumbnail_url ? (
           <div className="relative aspect-video bg-gray-200">
            <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" />
            {s.photo_count > 1 && (
             <span className="absolute bottom-1 right-1 bg-black bg-opacity-60 text-white text-[10px] px-1.5 py-0.5">
              +{s.photo_count - 1}
             </span>
            )}
           </div>
          ) : (
           <div className="aspect-video bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
            사진 없음
           </div>
          )}
          <div className="p-2 text-xs">
           {s.address ? (
            <div className="text-gray-700 line-clamp-2">{s.address}</div>
           ) : (
            <div className="text-gray-400">위치 미등록</div>
           )}
           {s.name && <div className="text-gray-400 mt-1 truncate">{s.name}</div>}
          </div>
         </button>
        ))}
       </div>
       {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
         <button
          onClick={() => setScreenPage(Math.max(0, screenPage - 1))}
          disabled={screenPage === 0}
          className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-50"
         >
          이전
         </button>
         <span className="text-sm text-gray-600">{screenPage + 1} / {totalPages}</span>
         <button
          onClick={() => setScreenPage(Math.min(totalPages - 1, screenPage + 1))}
          disabled={screenPage >= totalPages - 1}
          className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-50"
         >
          다음
         </button>
        </div>
       )}
      </div>
     );
    })()}

    {owner && (
     <div className="border-t border-gray-100 pt-4 mt-4">
      <h2 className="text-sm font-semibold text-gray-700 mb-2">소유자</h2>
      <div className="flex items-center gap-3 mb-3">
       <FamilyAvatar
        name={owner.name || owner.business_name || owner.mo_peer_hash}
        familyHash={owner.mo_peer_hash}
        imageUrl={owner.profile_image}
        size={40}
       />
       <div className="text-sm">
        <div className="font-medium">{owner.name || '-'}</div>
        {owner.business_name && <div className="text-xs text-gray-500">{owner.business_name}</div>}
       </div>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
       <div><dt className="text-gray-500">이메일</dt><dd>{owner.email ?? <span className="text-gray-400">로그인 후 노출</span>}</dd></div>
       <div><dt className="text-gray-500">전화</dt><dd>{owner.phone ?? <span className="text-gray-400">로그인 후 노출</span>}</dd></div>
       <div><dt className="text-gray-500">사업자번호</dt><dd>{owner.registration_number ?? <span className="text-gray-400">로그인 후 노출</span>}</dd></div>
       <div><dt className="text-gray-500">대표자</dt><dd>{owner.ceo_name ?? <span className="text-gray-400">로그인 후 노출</span>}</dd></div>
      </dl>
     </div>
    )}

    {/* 소유주 측 영상 — 제안 보내기 전에 상대가 뭘 틀고 있고 뭘 내보내는지 본다(2026-10-13).
        바터·구성원 모집은 "내보내는 중"이, 미디어사 매물은 "재생 중"이 핵심이라 둘 다 보여 준다.
        비로그인은 연락처와 같은 기준으로 숨긴다. */}
    <div className="border-t border-gray-100 pt-4 mt-4">
     <h2 className="text-sm font-semibold text-gray-700 mb-2">소유주 영상</h2>
     {!isLoggedIn ? (
      <p className="text-sm text-gray-400">로그인 후 노출</p>
     ) : (
      <div className="space-y-3">
       {([
        { key: 'playing', title: '자기 화면에 재생 중', items: ownerVideos.playing,
          empty: '지금 화면에 재생 중인 영상이 없습니다.' },
        { key: 'outbound', title: '외부 화면으로 내보내는 중', items: ownerVideos.outbound,
          empty: '지금 외부로 내보내는 영상이 없습니다.' },
       ] as { key: string; title: string; items: (Video & { screen_family_count?: number })[]; empty: string }[]).map(g => (
        <div key={g.key}>
         <div className="text-xs text-gray-500 mb-1">
          {g.title} <span className="text-gray-400">{g.items.length}개</span>
         </div>
         {g.items.length === 0 ? (
          <p className="text-xs text-gray-400">{g.empty}</p>
         ) : (
          <div className="flex flex-wrap gap-2">
           {g.items.map(v => (
            <button
             key={v.file_hash}
             type="button"
             onClick={() => { setPreviewFailed(false); setPreviewVideo(v); }}
             className="w-24 text-left hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-400"
             title={`${v.filename} — 클릭하면 미리보기`}
            >
             <div className="relative aspect-video border border-gray-200 bg-gray-100 overflow-hidden">
              {v.thumbnail_path ? (
               <img src={`/mofile/thumbnail/${v.thumbnail_path}`} alt={v.filename}
                loading="lazy" className="w-full h-full object-cover" />
              ) : null}
              {g.key === 'outbound' && (v.screen_family_count ?? 0) > 0 && (
               <span className="absolute bottom-0.5 right-0.5 bg-black/60 text-white text-[10px] px-1 rounded">
                {v.screen_family_count}곳
               </span>
              )}
             </div>
             <div className="text-[10px] text-gray-600 truncate mt-0.5">{v.filename}</div>
            </button>
           ))}
          </div>
         )}
        </div>
       ))}
      </div>
     )}
    </div>

    {isLoggedIn ? (
     <div className="border-t border-gray-100 pt-4 mt-4 flex items-center justify-between gap-2 flex-wrap">
      {(() => {
       const isMine = !!user && !!owner?.mo_peer_hash && user.moPeerHash === owner.mo_peer_hash;
       if (isMine) {
        return (
         <button
          onClick={() => setShowEditModal(true)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white"
         >수정</button>
        );
       }
       if (!listing.is_active || listing.is_expired) {
        return (
         <p className="text-sm text-gray-500">
          {listing.is_expired ? '만료된 리스팅이라 제안할 수 없습니다.' : '비활성 상태라 제안할 수 없습니다.'}
         </p>
        );
       }
       if (listing.side === 'seeking_member') {
        if (!owner?.email) return <p className="text-sm text-gray-500">수신자 정보를 확인할 수 없습니다.</p>;
        return (
         <button
          onClick={() => setShowInvitationModal(true)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white"
         >가입 제안 보내기</button>
        );
       }
       if (listing.side === 'barter') {
        if (!owner?.mo_peer_hash) return <p className="text-sm text-gray-500">수신자 정보를 확인할 수 없습니다.</p>;
        return (
         <button
          onClick={() => setShowBarterModal(true)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white"
         >바터 제안 보내기</button>
        );
       }
       if (owner) {
        return (
         <button
          onClick={() => setShowProposalModal(true)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white"
         >
          {listing.side === 'seeking_media' ? '스크린 제안' : '광고 제안'}
         </button>
        );
       }
       return null;
      })()}
      <ReportButton listing_hash={listing.listing_hash} />
     </div>
    ) : (
     <div className="border-t border-gray-100 pt-4 mt-4 text-sm">
      <button
       onClick={() => navigate('/login')}
       className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white"
      >
       로그인하고 제안하기
      </button>
     </div>
    )}
   </div>

   {showProposalModal && owner && (listing.side === 'seeking_advertiser' || listing.side === 'seeking_media') && (
    <ProposalCreate
     listing={{ ...listing, side: listing.side }}
     owner={owner}
     videos={videos}
     onClose={() => setShowProposalModal(false)}
     onCreated={(hash) => {
      setShowProposalModal(false);
      alert('제안을 보냈습니다.');
      navigate(`/market/proposals/${hash}`);
     }}
    />
   )}

   {showInvitationModal && owner && (
    <InvitationCreateModal
     toFamilyHash={listing.mo_family_hash}
     toFamilyName={family.name}
     recipientOwner={{
      mo_peer_hash: owner.mo_peer_hash,
      email: owner.email,
      name: owner.name,
     }}
     onClose={() => setShowInvitationModal(false)}
    />
   )}

   {showBarterModal && owner && (
    <BarterProposeModal
     listingHash={listing.listing_hash}
     listingTitle={listing.title}
     recipientPeerHash={owner.mo_peer_hash}
     onClose={() => setShowBarterModal(false)}
     onCreated={(_hash, conversationId) => {
      setShowBarterModal(false);
      alert('바터 제안을 보냈습니다.');
      navigate(`/inbox/${conversationId}`);
     }}
    />
   )}

   {showEditModal && (
    <ListingEditModal
     listing={listing}
     onClose={() => setShowEditModal(false)}
     onSaved={() => {
      setShowEditModal(false);
      window.location.reload();
     }}
    />
   )}

   <ScreenPublicModal clientHash={selectedScreen} onClose={() => setSelectedScreen(null)} />

   {previewVideo && (
    <div
     className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
     onClick={() => setPreviewVideo(null)}
     role="dialog"
     aria-modal="true"
    >
     <div
      className="bg-white w-full max-w-2xl rounded shadow-lg overflow-hidden"
      onClick={e => e.stopPropagation()}
     >
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
       <div className="text-sm font-medium truncate" title={previewVideo.filename}>{previewVideo.filename}</div>
       <button type="button" onClick={() => setPreviewVideo(null)} className="text-gray-500 hover:text-gray-800 text-sm ml-3">닫기</button>
      </div>
      <div className="bg-black flex items-center justify-center">
       {previewFailed ? (
        <div className="text-gray-300 text-sm py-16 px-4 text-center">
         이 영상은 브라우저에서 바로 재생할 수 없습니다. 썸네일로 확인해 주세요.
        </div>
       ) : (
        <video
         key={previewVideo.file_hash}
         controls
         autoPlay
         playsInline
         src={`/market/listings/${listing.listing_hash}/preview/${previewVideo.file_hash}`}
         poster={previewVideo.thumbnail_path ? `/mofile/thumbnail/${previewVideo.thumbnail_path}` : undefined}
         onError={() => setPreviewFailed(true)}
         className="w-full max-h-[70vh]"
        />
       )}
      </div>
      {typeof previewVideo.duration === 'number' && previewVideo.duration > 0 && (
       <div className="px-3 py-2 text-xs text-gray-500">길이 {Math.round(previewVideo.duration)}초</div>
      )}
     </div>
    </div>
   )}
  </div>
 );
};

export default ListingDetail;
